<template>
  <div class="page-container">
    <template v-if="!loadingPaymentMethod && !loadingActiveSubscription">
      <div class="page-container-body">
        <h3 class="page-title">Billing</h3>

        <div class="billing-info">
          <div class="info-row">
            <v-icon size=large>mdi-account-credit-card-outline</v-icon>
            <div class="info-row-label">
              <template v-if="hasCardOnFile">
                <span class="text-capitalize">{{ paymentMethod?.card?.displayBrand }}</span> ending
                ****{{ paymentMethod?.card?.last4 }}
              </template>
              <template v-else>
                No payment method on file
              </template>
            </div>
          </div>
          <div class="info-row" v-if="subscriptionAccessType === 'FULL'">
            <v-icon size=large>mdi-calendar</v-icon>
            <div class="d-flex flex-md-column">
              <div v-if="hasUnchosenTrial">
                Free trial, ends on {{ nextBillingDate }}. You'll add your card when you choose your plan.
              </div>
              <div
                v-else-if="!!activeSubscription?.subscriptionDetails?.cancelAt && !activeSubscription?.scheduleSubscriptionDetails">
                Your subscription will expire on {{ nextBillingDate }}.
              </div>
              <template v-else>
                <div class="info-row-label">
                  ${{
                    scheduledPlan ? scheduledNextBillingAmount : activeSubscription?.subscriptionDetails?.nextBillingAmount
                  }}/{{ selectedSubscriptionType === 'monthly' ? 'mo' : 'ye' }} (excluding tax)
                </div>
                <div class="info-row-label">
                  Next payment on {{
                    scheduledPlan ? scheduledNextBillingDate : nextBillingDate
                  }}
                </div>
              </template>
            </div>
          </div>
          <div class="info-row">
            <RouterLink to="/subscription/billing/billing-history" class="viewBillingHistory">
              View billing history
            </RouterLink>
          </div>
        </div>

        <div v-if="hasUnchosenTrial || hasEndedUnchosenTrial">
          <v-btn
            variant="outlined"
            size="large"
            @click="$router.push('/plans')">
            Choose your plan
          </v-btn>
        </div>
        <div v-else-if="!isSubscriptionCanceled">
          <v-btn
            variant="outlined"
            size="large"
            @click="$router.push('/subscription/billing/manage-billing')">
            {{ hasCardOnFile ? 'Replace Payment Method' : 'Add Payment Method' }}
          </v-btn>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="page-loading-container-body">
        <v-progress-circular
          indeterminate
          color="white"
          size="32"
          width="2">
        </v-progress-circular>
        <span>Loading billing information...</span>
      </div>
    </template>
  </div>
</template>

<script>
import format from 'date-fns/format'
import { mapState } from 'vuex'
import { getPlanByPriceId, getSubscriptionTypeByPriceId, hasEndedUnchosenTrial, hasUnchosenTrial } from '@/utils/payments'

export default {
  computed: {
    ...mapState({
      currentUser: state => state.user.user,
      activeSubscription: state => state.payments.activeSubscription,
      paymentMethod: state => state.payments.paymentMethod,
      subscriptionAccessType: state => state.payments.subscriptionAccessType,
      loadingPaymentMethod: state => state.payments.loadingPaymentMethod,
      loadingActiveSubscription: state => state.payments.loadingActiveSubscription,
      isSubscriptionAdmin: state => state.payments.isSubscriptionAdmin
    }),
    hasCardOnFile: function () {
      return !!this.paymentMethod?.card?.last4
    },
    hasUnchosenTrial: function () {
      return hasUnchosenTrial(this.activeSubscription)
    },
    hasEndedUnchosenTrial: function () {
      return hasEndedUnchosenTrial(this.activeSubscription)
    },
    isSubscriptionCanceled: function () {
      return !!this.activeSubscription?.subscriptionDetails?.cancelAt &&
        !this.activeSubscription?.scheduleSubscriptionDetails
    },
    scheduledPlan: function () {
      const scheduledPriceId = this.activeSubscription?.scheduleSubscriptionDetails?.phases?.[0]?.items?.[0]?.priceId
      if (scheduledPriceId) {
        return getPlanByPriceId(scheduledPriceId)
      }
      return null
    },
    nextBillingDate: function () {
      const nextBillingDate = this.activeSubscription?.subscriptionDetails?.nextBillingDate
      if (nextBillingDate) {
        return format(new Date(nextBillingDate), 'MMMM dd, yyyy')
      }
      return ''
    },
    scheduledNextBillingDate: function () {
      const startDate = this.activeSubscription?.scheduleSubscriptionDetails?.phases?.[0]?.startDate
      if (startDate) {
        return format(new Date(startDate), 'MMMM dd, yyyy')
      }
      return ''
    },
    scheduledNextBillingAmount: function () {
      const scheduleQuantity = this.activeSubscription?.scheduleSubscriptionDetails?.phases?.[0]?.items?.[0]?.quantity
      if (this.scheduledPlan) {
        const subscriptionType = this.scheduledSubscriptionType
        return scheduleQuantity * (subscriptionType === 'monthly' ? this.scheduledPlan.monthlyPrice : this.scheduledPlan.yearlyPrice)
      }
      return 0
    },
    subscriptionType: function () {
      if (this.activeSubscription) {
        return getSubscriptionTypeByPriceId(this.activeSubscription?.subscriptionDetails?.price?.id)
      }
      return null
    },
    scheduledSubscriptionType: function () {
      const scheduledPriceId = this.activeSubscription?.scheduleSubscriptionDetails?.phases?.[0]?.items?.[0]?.priceId
      if (scheduledPriceId) {
        return getSubscriptionTypeByPriceId(scheduledPriceId)
      }
      return null
    },
    selectedSubscriptionType: function () {
      return this.scheduledPlan ? this.scheduledSubscriptionType : this.subscriptionType
    }
  },
  watch: {
    isSubscriptionAdmin: function (val) {
      if (val === false) {
        this.$router.push('/subscription/my-plan')
      }
    }
  }
}
</script>

<style scoped>
.page-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.page-container-body {
  flex-grow: 1;
  padding: 0 4px;
  overflow: auto;
}

.page-loading-container-body {
  gap: 16px;
  height: 100%;
  display: flex;
  font-size: 16px;
  font-weight: 400;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  color: var(--dark-4, #363A40);
}

.page-title {
  margin-bottom: 32px;
  font-size: 20px;
  font-weight: 700;
  color: var(--dark-4, #363A40);
}

.billing-info {
  gap: 16px;
  display: flex;
  margin-bottom: 32px;
  flex-direction: column;
}

.info-row {
  gap: 8px;
  display: flex;
  align-items: center;
  color: var(--dark-3, #61676E);
}

.info-row-label {
  font-size: 14px;
  font-weight: 400;
  line-height: 16.8px;
  color: var(--dark-3, #61676E);
}

.viewBillingHistory {
  font-size: 12px;
  cursor: pointer;
  font-weight: 500;
  color: var(--blue-4, #0D66D0);
}
</style>
